const TRANSPARENT_COLOR = 'rgba(0,0,0,0)';

/**
 * Map tokens to css-friendly properties. and split into
 * groups (`theme`, `isPill`, `text`, `size`) so they can be
 * conditionally applied depending on their namesake props
 *
 * @param {{}} tokens system tokens. At least theme, font, border, and space
 */
const mapTokensToProps = ({ theme, appearance, variant, isNative = false }) => {
  const themeProps = tokenizeAppearanceAndThemeProps({
    ...theme,
    appearance,
    variant
  });
  const insetBuilder = base => {
    if (isNative) {
      return {
        insetTop: base / 2,
        insetRight: base,
        insetBottom: base / 2,
        insetLeft: base
      };
    }
    return {
      insetTop: `${base / 2}rem`,
      insetRight: `${base}rem`,
      insetBottom: `${base / 2}rem`,
      insetLeft: `${base}rem`
    };
  };
  const font = theme.font;
  return {
    theme: {
      ...themeProps
    },
    isPill: isNative
      ? [4, 32]
      : ['0.25rem', '2rem', theme.size.border.radius.fiveX],
    text: {
      fontFamily: font.base.family,
      fontStyle: 'normal',
      letterSpacing: 0,
      fontWeight: `${font.base.weight.regular}`
    },
    size: {
      s: {
        fontSize: font.base.size.s,
        iconSize: 's',
        minWidth: 96, // no token for this
        lineHeight: 1, // no token for this
        ...insetBuilder(isNative ? 16 : 1)
      },
      m: {
        fontSize: font.base.size.m,
        iconSize: 'm',
        minWidth: 112, // no token for this
        lineHeight: 1.125, // no token for this
        ...insetBuilder(isNative ? 16 : 1)
      },
      l: {
        fontSize: font.base.size.m,
        iconSize: 'm',
        minWidth: 128, // no token for this
        lineHeight: 1.125, // no token for this
        ...insetBuilder(isNative ? 24 : 1.5)
      }
    },
    iconOnly: {
      s: {
        iconSize: 'l',
        minWidth: 0, // no tokens for these
        insetTop: 3,
        insetRight: 3,
        insetBottom: 3,
        insetLeft: 3
      },
      m: {
        iconSize: 'l',
        minWidth: 0, // no tokens for these
        insetTop: 5,
        insetRight: 5,
        insetBottom: 5,
        insetLeft: 5
      },
      l: {
        iconSize: 'l',
        minWidth: 0, // no tokens for these
        insetTop: 9,
        insetRight: 9,
        insetBottom: 9,
        insetLeft: 9
      }
    },
    border: {
      default: theme.size.border.base,
      focus: theme.size.border.focus
    }
  };
};

/**
 * By appearance, create a mapping of tokens to css properties to use
 * as inline styles on web or mobile. Requires tokens have matching keys defined for each
 * theme.
 *
 * @param {{ [textColor]: object }} textColor the textColor object in the top-level `theme` object
 * @param {{ [interactiveColor]: object }} interactiveColor the interactiveColor object in the top-level `theme` object
 * @returns {{}} {appearanceNames > css properties }
 */
function tokenizeAppearanceAndThemeProps({
  font,
  backgroundColor,
  borderColor,
  appearance,
  variant
}) {
  const variantCollection = {};
  if (variant && variant !== '') {
    variantCollection[`${variant}_${appearance}`] = {
      ...createButtonVariant(
        font,
        backgroundColor,
        borderColor,
        appearance,
        variant
      )
    };
  }

  return {
    ...variantCollection,
    filled: {
      ...createButtonVariant(
        font,
        backgroundColor,
        borderColor,
        appearance,
        variant
      )
    },
    lined: {
      ...createButtonVariant(
        font,
        backgroundColor,
        borderColor,
        appearance,
        variant
      )
    },
    ghost: {
      ...createButtonVariant(
        font,
        backgroundColor,
        borderColor,
        appearance,
        variant
      )
    }
  };
}

function createButtonVariant(
  font,
  backgroundColor,
  borderColor,
  appearance,
  variant
) {
  const defaultButton = {
    textColorDisabled: font.color.disabled,
    backgroundColor: 'transparent',
    backgroundColorDisabled: backgroundColor.disabled,
    borderColorDisabled: borderColor.disabled,
    outlineColor: TRANSPARENT_COLOR
  };

  if (appearance === 'filled') {
    return createFilledButton(
      font,
      backgroundColor,
      borderColor,
      defaultButton,
      variant
    );
  } else if (appearance === 'lined') {
    return createLinedButton(
      font,
      backgroundColor,
      borderColor,
      defaultButton,
      variant
    );
  }
  return createGhostButton(
    font,
    backgroundColor,
    borderColor,
    defaultButton,
    variant
  );
}

function createFilledButton(
  font,
  backgroundColor,
  borderColor,
  defaultButton,
  variant
) {
  let useVariant = 'interactive';
  if (variant && variant !== '') {
    useVariant = variant;
  }
  return {
    ...defaultButton,
    textColor: font.color[useVariant].fill,
    textColorHover: font.color[useVariant].fill,
    textColorActive: font.color[useVariant].fill,

    backgroundColor: backgroundColor[useVariant].fill.base,
    backgroundColorHover: backgroundColor[useVariant].fill.hover,
    backgroundColorActive: backgroundColor[useVariant].fill.active,
    backgroundColorDisabled: backgroundColor[useVariant].fill.disabled,

    borderColor: borderColor[useVariant].base,
    borderColorHover: borderColor[useVariant].hover,
    borderColorActive: borderColor[useVariant].active,
    borderColorDisabled: borderColor[useVariant].disabled,

    outlineColorFocus: borderColor[useVariant].focus
  };
}

function createLinedButton(
  font,
  backgroundColor,
  borderColor,
  defaultButton,
  variant
) {
  let useVariant = 'interactive';
  if (variant && variant !== '') {
    useVariant = variant;
  }
  return {
    ...defaultButton,
    textColor: font.color[useVariant].base,
    textColorHover: font.color[useVariant].fill,
    textColorActive: font.color[useVariant].fill,

    backgroundColorHover: backgroundColor[useVariant].fill.hover,
    backgroundColorActive: backgroundColor[useVariant].fill.active,

    borderColor: borderColor[useVariant].base,
    borderColorHover: borderColor[useVariant].hover,
    borderColorActive: borderColor[useVariant].active,
    borderColorDisabled: borderColor[useVariant].disabled,

    outlineColorFocus: borderColor[useVariant].focus
  };
}

function createGhostButton(
  font,
  backgroundColor,
  borderColor,
  defaultButton,
  variant
) {
  let useVariant = 'interactive';
  if (variant && variant !== '') {
    useVariant = variant;
  }
  return {
    ...defaultButton,
    textColor: font.color[useVariant].base,
    textColorHover: font.color[useVariant].fill,
    textColorActive: font.color[useVariant].fill,

    backgroundColorHover: backgroundColor[useVariant].fill.hover,
    backgroundColorActive: backgroundColor[useVariant].fill.active,

    borderColor: 'transparent',
    borderColorHover: borderColor[useVariant].hover,
    borderColorActive: borderColor[useVariant].active,

    outlineColorFocus: borderColor[useVariant].focus
  };
}

export default mapTokensToProps;
